import { Injectable } from '@angular/core';
import { AccountResponse } from '../model/response/account-response';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { catchError, Observable, of, Subject, tap } from 'rxjs';
import { AccountRequest } from '../model/request/account-request';
import { Account } from '../model/form/account';
import { enviroment } from 'src/environments/environment';
import { LanguageEnum } from '../model/enum/language-enum';
import { EN, ES, PT } from '../model/mockup/language-mockup';
import { UserResponseDto } from '../model/response/UserResponseDto';
import { DocType } from '../model/doc-type';
import { Reason } from '../model/reason';
import { FindUserVcusGetResp } from '../model/response/find-user-vcus-get-resp';
import { FindUserEmailGetResp } from '../model/response/find-user-email-get-resp';
import { PhoneCountry } from '../model/country';
import { PublicAccountResponse } from '../model/response/public-account-response';
import { TokenPurchaseCheckoutReq, TokenPurchaseCheckoutResp } from '../model/response/token-package.model';
import { UserDataExportStatus } from '../model/user-data-export';

@Injectable({providedIn: 'root'})
export class AccountService {

  httpOptions: {};
  accountUrl: string;
  accountRequest: AccountRequest;

  /**
   * Emite quando algum estado relevante da conta muda (ex.: virou tuner).
   * Componentes que cacheiam dados da conta (dashboard-template, header) devem
   * se inscrever e re-buscar via getAccount.
   */
  private _accountChanged$ = new Subject<void>();
  readonly accountChanged$ = this._accountChanged$.asObservable();

  constructor(private http: HttpClient) {
    this.httpOptions = {};
    this.accountUrl = enviroment.BASE_URL_API + '/user';
    this.accountRequest = new AccountRequest();
  }

  /** Sinaliza pros listeners que devem re-buscar os dados da conta. */
  notifyAccountChanged(): void {
    this._accountChanged$.next();
  }

  
  getAccount(userId: string) : Observable<AccountResponse> {
    return this.http.get<AccountResponse>(this.accountUrl + '/'+ userId, this.httpOptions).pipe(
      tap()
    );
  }

  /**
   * ZIP com os dados da conta, as faturas e o XML das notas emitidas. Mapas e logs não
   * entram: são baixados pela aba Arquivos.
   * Contrato: ftcloud-api/docs/integracoes/frontend-angular/ANGULAR_EXPORTACAO_DE_DADOS.md.
   */
  exportData(userId: string): Observable<Blob> {
    return this.http.get(this.accountUrl + '/' + userId + '/export', { responseType: 'blob' });
  }

  /** Estado da exportação: habilita o botão e traz as datas da janela de regeração. */
  getExportStatus(userId: string): Observable<UserDataExportStatus> {
    return this.http.get<UserDataExportStatus>(this.accountUrl + '/' + userId + '/export/status');
  }

  getPublicAccount(userId: string) : Observable<PublicAccountResponse> {
    return this.http.get<PublicAccountResponse>(this.accountUrl + '/public/'+ userId, this.httpOptions).pipe(
      tap(),
      catchError(this.handleError<PublicAccountResponse>(`getPublicAccount`))
    );
  }
  
  findUserActive() : Observable<UserResponseDto> {
    return this.http.get<UserResponseDto>(this.accountUrl + '/active', this.httpOptions).pipe(
      tap(),
      catchError(this.handleError<UserResponseDto>(`findUserActive`))
    );
  }

  updateAccount(account: Account) : Observable<AccountResponse> {
    let accountRequest = this.toUpdtAccountRequest(account);
    return this.http.put<AccountResponse>(this.accountUrl + '/'+ account.id, accountRequest, this.httpOptions).pipe(
      tap()
    );
  }

  updateAccountFix(accountRequest: AccountRequest) : Observable<AccountResponse> {

    return this.http.put<AccountResponse>(this.accountUrl + '/'+ accountRequest.userId, accountRequest, this.httpOptions).pipe(
      tap(),
      catchError(this.handleError<AccountResponse>('updateAccount'))
    );
  }

  updateUser(account: AccountResponse) : Observable<AccountResponse> {
    return this.http.put<AccountResponse>(this.accountUrl + '/'+ account.userId, account, this.httpOptions).pipe(
      tap()
    );
  }

  toAccountRequest(acc: Account, inactiveReason: Reason | null) : AccountRequest {
    let lan;
    
    switch (acc.language) {
      case EN:
        lan = LanguageEnum.EN_US
        break;
      case PT:
        lan = LanguageEnum.PT_BR
        break;
      case ES:
        lan = LanguageEnum.ES_ES
        break;
    
      default:
        lan = LanguageEnum.PT_BR;
        break;
    }
    if(acc) {
      //let phoneComplete = acc.phoneCountry.dialCode.replace('+', '')+'-'+acc.phone;
      return new AccountRequest(
            acc.id,
            acc.country, 
            acc.name, 
            acc.docNumber, 
            (acc.birthDate ? acc.birthDate.toISOString().split('T')[0] : null), 
            acc.phone, 
            acc.phoneCountry.dialCode.replace('+', ''),
            acc.phoneCountry.id.toString(),
            acc.email, 
            lan, 
            acc.checkNewsletter, 
            acc.inactive, 
            inactiveReason,
            acc.firstAccess);
    } else {
      return new AccountRequest();
    }
  }

  toUpdtAccountRequest(acc: Account) : AccountRequest {
    let lan;
    switch (acc.language) {
      case EN:
      lan = LanguageEnum.EN_US;
      break;
      case PT:
      lan = LanguageEnum.PT_BR;
      break;
      case ES:
      lan = LanguageEnum.ES_ES;
      break;
      default:
      lan = LanguageEnum.PT_BR;
      break;
    }
    if(acc) {
      //let phoneComplete = acc.phoneCountry.dialCode.replace('+', '')+'-'+acc.phone;
      return new AccountRequest(
            acc.id,
            acc.country, 
            acc.name, 
            acc.docNumber, 
            (acc.birthDate ? acc.birthDate.toISOString().split('T')[0] : null), 
            acc.phone, 
            acc.phoneCountry.dialCode.replace('+', ''),
            acc.phoneCountry.id.toString(),
            acc.email, 
            lan, 
            acc.checkNewsletter, 
            acc.inactive, 
            acc.inactiveReason,
            acc.firstAccess);
    } else {
      return new AccountRequest();
    }
  }

  toAccountRequestFix(acc: Account, docNumber: string, email: string, inactiveReason: Reason | null) : AccountRequest {
    let lan;
    switch (acc.language) {
      case EN:
      lan = LanguageEnum.EN_US;
      break;
      case PT:
      lan = LanguageEnum.PT_BR;
      break;
      case ES:
      lan = LanguageEnum.ES_ES;
      break;
      default:
      lan = LanguageEnum.PT_BR;
      break;
    }
    if(acc) {
      return new AccountRequest(
            acc.id,
            acc.country, 
            acc.name, 
            docNumber, 
            (acc.birthDate ? acc.birthDate.toISOString().split('T')[0] : null), 
            acc.phone, 
            acc.phoneCountry.dialCode.replace('+', ''),
            acc.phoneCountry.id.toString(),
            email, 
            lan, 
            acc.checkNewsletter, 
            acc.inactive, 
            inactiveReason,
            acc.firstAccess);
    } else {
      return new AccountRequest();
    }
  }

  static toAccount(acc: AccountResponse) : Account {
    let { phone } =  AccountService.separePhoneToDialCode(acc.phone);
    let dialCode = '+' + acc.dialCode
    
    if(acc) {
      return new Account(
        acc.userId,
        acc.country, 
        acc.name, 
        (acc.docNumber.length === 11 ? DocType.CPF : DocType.CNPJ),
        acc.docNumber,
        (acc.birthDate ? AccountService.convertDateToUTC(new Date(acc.birthDate)) : null), 
        phone, 
        { dialCode: dialCode, id: +acc.phoneCountry } as PhoneCountry,
        acc.email, 
        '',
        '',
        (LanguageEnum.EN_US === acc.language ? EN : (LanguageEnum.ES_ES === acc.language ? ES : PT)), 
        true,
        acc.checkNewsletter, 
        acc.inactive, 
        acc.inactiveReason,
        !!acc.accessLevel);
    } else {
      return new Account();
    }
  }

  static separePhoneToDialCode(phoneComplete: string) {
    let phone = '';

    if (phoneComplete.includes('-')) {
        const parts = phoneComplete.split('-');
        phone = parts[1]; 
    } else {
      phone = phoneComplete;
    }
    // } else {
    //     if (phoneComplete.startsWith("1")) {
    //         dialCode = "+1";
    //         phone = phoneComplete.substring(1);
    //     } else if (phoneComplete.startsWith("55")) {
    //         dialCode = "+55";
    //         phone = phoneComplete.substring(2);
    //     } else if (phoneComplete.startsWith("54")) {
    //       dialCode = "+54";
    //       phone = phoneComplete.substring(2);
    //   }
    // }

    return { phone };
}

  static convertDateToUTC(date: Date) { 
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()); 
  }

  getLanguage(lan: string | null) {
    return (lan ? lan : LanguageEnum.PT_BR);
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
  
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead
  
      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);
  
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  FindUserVcus(userID: string, statusVcu?:string) : Observable<FindUserVcusGetResp[]> {
    if(statusVcu){
      return this.http.get<FindUserVcusGetResp[]>(this.accountUrl + '/'+ userID + '/vcu?statusVcu='+ statusVcu, this.httpOptions).pipe(
        tap(),
        catchError(this.handleError<FindUserVcusGetResp[]>(`FindUserVcus`))
      );
    }else{
      return this.http.get<FindUserVcusGetResp[]>(this.accountUrl + '/'+ userID + '/vcu', this.httpOptions).pipe(
        tap(),
        catchError(this.handleError<FindUserVcusGetResp[]>(`FindUserVcus`))
      );
    }
  }

  FindUserEmail(email: string) : Observable<FindUserEmailGetResp> {
    return this.http.get<FindUserEmailGetResp>(this.accountUrl + '/email/'+ email, this.httpOptions).pipe(
      tap()
    );
  }

  FindUserVcuSerial(userID: string, serial: string, status: string) : Observable<FindUserVcusGetResp[]> {
    let url = `${this.accountUrl}/${userID}/vcu/${serial}?statusVcu=${status}`
    return this.http.get<FindUserVcusGetResp[]>(url, this.httpOptions).pipe(tap());
  }

  checkoutSubscription(
    userId: string,
    planId: string,
    billingCycle: 'monthly' | 'annual',
    tunerId?: string | null,
    tunerInviteCode?: string | null
  ): Observable<{ checkoutUrl?: string; upgraded?: boolean; scheduled?: boolean; effectiveAt?: string }> {
    const body: any = { planId, billingCycle };
    if (tunerId) body.tunerId = tunerId;
    if (tunerInviteCode) body.tunerInviteCode = tunerInviteCode;
    return this.http.post<{ checkoutUrl?: string; upgraded?: boolean; scheduled?: boolean; effectiveAt?: string }>(
      `${this.accountUrl}/${userId}/subscription/checkout`,
      body
    );
  }

  checkoutTokenPackage(userId: string, packageId: string): Observable<TokenPurchaseCheckoutResp> {
    return this.http.post<TokenPurchaseCheckoutResp>(
      `${this.accountUrl}/${userId}/tokens/checkout`,
      { packageId } as TokenPurchaseCheckoutReq
    );
  }

  getSubscriptionPortal(userId: string, returnUrl?: string): Observable<{ portalUrl: string }> {
    const url = returnUrl
      ? `${this.accountUrl}/${userId}/subscription/portal?returnUrl=${encodeURIComponent(returnUrl)}`
      : `${this.accountUrl}/${userId}/subscription/portal`;
    return this.http.post<{ portalUrl: string }>(url, {});
  }


    static toAllAccounts(acc: AccountResponse) : Account {
    // let { phone } = AccountService.separePhoneToDialCode(acc.phone);
    let dialCode = '+' + acc.dialCode
    
    if(acc) {
      return new Account(
        acc.userId,
        acc.country, 
        acc.name, 
        (acc.docNumber.length === 11 ? DocType.CPF : DocType.CNPJ),
        acc.docNumber,
        (acc.birthDate ? AccountService.convertDateToUTC(new Date(acc.birthDate)) : null), 
        "", 
        { dialCode: dialCode, id: +acc.phoneCountry } as PhoneCountry,
        acc.email, 
        '',
        '',
        (LanguageEnum.EN_US === acc.language ? EN : (LanguageEnum.ES_ES === acc.language ? ES : PT)), 
        true,
        acc.checkNewsletter, 
        acc.inactive, 
        acc.inactiveReason,
        !!acc.accessLevel);
    } else {
      return new Account();
    }
  }

}
